import "@/App.css";
import "@/i18n";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AuthCallback } from "@/components/AuthCallback";
import { TranslationProvider } from "@/context/TranslationContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { TourProvider } from "@/components/GuidedTour";
import { LocaleUrlSync } from "@/components/LocaleUrlSync";
import "@/lib/authInterceptor"; // axios interceptor for Auth0 bearer
import { ErioAuth0Provider } from "@/lib/auth0Provider";
import Auth0Callback from "@/pages/Auth0Callback";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Docs from "@/pages/Docs";
import Logout from "@/pages/Logout";
import SsoCallback from "@/pages/SsoCallback";
import Dashboard from "@/pages/Dashboard";
import Explore from "@/pages/Explore";
import RiskImpact from "@/pages/RiskImpact";
import PackArchive from "@/pages/PackArchive";
import RequestAccess from "@/pages/RequestAccess";
import AccessRequestsQueue from "@/pages/AccessRequestsQueue";
import BoardPackView from "@/pages/BoardPackView";
import Risks from "@/pages/Risks";
import Frameworks from "@/pages/Frameworks";
import Compliance from "@/pages/Compliance";
import Resilience from "@/pages/Resilience";
import ScenarioStudio from "@/pages/ScenarioStudio";
import Alerts from "@/pages/Alerts";
import Interop from "@/pages/Interop";
import Integrations from "@/pages/Integrations";
import Ingestion from "@/pages/Ingestion";
import Calibration from "@/pages/Calibration";
import Geopolitics from "@/pages/Geopolitics";
import Trends from "@/pages/Trends";
import ControlROI from "@/pages/ControlROI";
import Appetite from "@/pages/Appetite";
import Onboarding from "@/pages/Onboarding";
import Organisation from "@/pages/Organisation";
import Playbooks from "@/pages/Playbooks";
import PlaybookLibrary from "@/pages/PlaybookLibrary";
import Remediation from "@/pages/Remediation";
import Suppliers from "@/pages/Suppliers";
import Help from "@/pages/Help";
import Signals from "@/pages/Signals";
import Admin from "@/pages/Admin";
import Users from "@/pages/Users";
import Governance from "@/pages/Governance";
import AuditLog from "@/pages/AuditLog";
import BriefingLedger from "@/pages/BriefingLedger";
import Leads from "@/pages/Leads";
import Tenants from "@/pages/Tenants";
import SalesDocs from "@/pages/SalesDocs";
import Automation from "@/pages/Automation";
import OwnerReporting from "@/pages/OwnerReporting";
import ComplianceOps from "@/pages/ComplianceOps";
import StrategicOps from "@/pages/StrategicOps";
import Roadmap from "@/pages/Roadmap";
import NpsLanding from "@/pages/NpsLanding";
import DriftWatchOverview from "@/pages/DriftWatchOverview";
import Connectors from "@/pages/Connectors";
import Catalogue from "@/pages/Catalogue";
import Security from "@/pages/Security";
import ComplianceReadiness from "@/pages/ComplianceReadiness";
import Provisioning from "@/pages/Provisioning";
import AccessPolicy from "@/pages/AccessPolicy";
import HowItWorks from "@/pages/HowItWorks";
import { CookieConsent } from "@/components/CookieConsent";
import Privacy from "@/pages/Privacy";
import Trust from "@/pages/Trust";
import Verify from "@/pages/Verify";
import Brief from "@/pages/Brief";
import BookDemo from "@/pages/BookDemo";
import BookPilot from "@/pages/BookPilot";
import ContactSales from "@/pages/ContactSales";
import Manual from "@/pages/Manual";
import Methodology from "@/pages/Methodology";
import MaturityAssessment from "@/pages/MaturityAssessment";
import RiskHealthCheck from "@/pages/RiskHealthCheck";
import PlanHealthCheck from "@/pages/PlanHealthCheck";
import SupplierRiskLens from "@/pages/SupplierRiskLens";
import PlanIntegrity from "@/pages/PlanIntegrity";
import PlanPortfolio from "@/pages/PlanPortfolio";
import { ProgrammesList, ProgrammeDetail } from "@/pages/Programmes";
import RiskRegisterQuality from "@/pages/RiskRegisterQuality";
import Status from "@/pages/Status";
import RiskDecisions from "@/pages/RiskDecisions";
import Benchmarks from "@/pages/Benchmarks";
import NotFound from "@/pages/NotFound";
import Terms from "@/pages/Terms";
import Changelog from "@/pages/Changelog";

// Renders the Google OAuth callback when returning from Emergent Auth with a
// #session_id fragment — checked synchronously during render (prevents races).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function HashSessionGate({ children }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <ErioAuth0Provider>
        <AuthProvider>
        <TranslationProvider>
        <BrowserRouter>
          <LocaleUrlSync />
          <TourProvider>
          <HashSessionGate>
          <Routes>
            <Route path="/" element={<HowItWorks />} />
            <Route path="/request-access" element={<RequestAccess />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/sso/callback" element={<SsoCallback />} />
            <Route path="/auth/callback" element={<Auth0Callback />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/trust" element={<Trust />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/brief" element={<Brief />} />
            <Route path="/demo" element={<BookDemo />} />
            <Route path="/pilot" element={<BookPilot />} />
            {/* /billing and /pricing are legacy inbound links following the
                2026-02 Stripe removal (ROADMAP §26h). Soft-land them onto the
                Contact-sales lead-capture page rather than a bare 404. */}
            <Route path="/contact-sales" element={<ContactSales />} />
            <Route path="/billing" element={<ContactSales />} />
            <Route path="/pricing" element={<ContactSales />} />
            <Route path="/maturity-assessment" element={<MaturityAssessment />} />
            <Route path="/risk-health-check" element={<RiskHealthCheck />} />
            <Route path="/plan-health-check" element={<PlanHealthCheck />} />
            <Route path="/supplier-risk-lens" element={<SupplierRiskLens />} />
            <Route path="/status" element={<Status />} />
            <Route path="/benchmarks" element={<Benchmarks />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/catalogue" element={<Catalogue />} />
            {/* iter156 — French URL-prefixed routes for public pages.
                Same component tree; locale resolution is handled by
                LocaleUrlSync + i18n. Catalogue / HowItWorks / Trust render
                in EN until iter157 extracts their strings. */}
            <Route path="/fr" element={<HowItWorks />} />
            <Route path="/fr/" element={<HowItWorks />} />
            <Route path="/fr/login" element={<Login />} />
            <Route path="/fr/how-it-works" element={<HowItWorks />} />
            <Route path="/fr/trust" element={<Trust />} />
            <Route path="/fr/catalogue" element={<Catalogue />} />
            <Route path="/fr/nps" element={<NpsLanding />} />
            <Route path="/de/login" element={<Login />} />
            <Route path="/de/how-it-works" element={<HowItWorks />} />
            <Route path="/de/trust" element={<Trust />} />
            <Route path="/de/catalogue" element={<Catalogue />} />
            <Route path="/de/nps" element={<NpsLanding />} />
            <Route path="/es/login" element={<Login />} />
            <Route path="/es/how-it-works" element={<HowItWorks />} />
            <Route path="/es/trust" element={<Trust />} />
            <Route path="/es/catalogue" element={<Catalogue />} />
            <Route path="/es/nps" element={<NpsLanding />} />
            <Route
              path="/help"
              element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plan-integrity"
              element={
                <ProtectedRoute permission="risk.read">
                  <PlanIntegrity />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plan-portfolio"
              element={
                <ProtectedRoute permission="risk.read">
                  <PlanPortfolio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programmes"
              element={
                <ProtectedRoute permission="risk.read">
                  <ProgrammesList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programmes/:id"
              element={
                <ProtectedRoute permission="risk.read">
                  <ProgrammeDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/risk-register-quality"
              element={
                <ProtectedRoute permission="risk.read">
                  <RiskRegisterQuality />
                </ProtectedRoute>
              }
            />
            <Route
              path="/manual"
              element={
                <ProtectedRoute>
                  <Manual />
                </ProtectedRoute>
              }
            />
            <Route
              path="/methodology"
              element={
                <ProtectedRoute superuser>
                  <Methodology />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/explore"
              element={
                <ProtectedRoute>
                  <Explore />
                </ProtectedRoute>
              }
            />
            <Route
              path="/explore/risks/:id/impact"
              element={
                <ProtectedRoute>
                  <RiskImpact />
                </ProtectedRoute>
              }
            />
            <Route
              path="/board-pack/:date"
              element={
                <ProtectedRoute>
                  <BoardPackView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pack-archive"
              element={
                <ProtectedRoute>
                  <PackArchive />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/access-requests"
              element={
                <ProtectedRoute>
                  <AccessRequestsQueue />
                </ProtectedRoute>
              }
            />
            <Route
              path="/docs"
              element={
                <ProtectedRoute>
                  <Docs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/docs/:slug"
              element={
                <ProtectedRoute>
                  <Docs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/risks"
              element={
                <ProtectedRoute permission="risk.read">
                  <Risks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/frameworks"
              element={
                <ProtectedRoute permission="framework.read">
                  <Frameworks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance"
              element={
                <ProtectedRoute permission="framework.read">
                  <Compliance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resilience"
              element={
                <ProtectedRoute permission="risk.read" domain="t_operational">
                  <Resilience />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scenario-studio"
              element={
                <ProtectedRoute permission="risk.read">
                  <ScenarioStudio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute permission="risk.read">
                  <Alerts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/interop"
              element={
                <ProtectedRoute permission="framework.read">
                  <Interop />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations"
              element={
                <ProtectedRoute permission="integration.manage">
                  <Integrations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ingestion"
              element={
                <ProtectedRoute permission="signal.read">
                  <Ingestion />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calibration"
              element={
                <ProtectedRoute permission="risk.read">
                  <Calibration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute anyPermission={["risk.write", "ntier.write"]}>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/appetite"
              element={
                <ProtectedRoute permission="risk.read">
                  <Appetite />
                </ProtectedRoute>
              }
            />
            <Route
              path="/control-roi"
              element={
                <ProtectedRoute permission="risk.read">
                  <ControlROI />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trends"
              element={
                <ProtectedRoute permission="risk.read">
                  <Trends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/geopolitics"
              element={
                <ProtectedRoute permission="risk.read" domain="t_geopolitical">
                  <Geopolitics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organisation"
              element={
                <ProtectedRoute permission="risk.read">
                  <Organisation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playbooks"
              element={
                <ProtectedRoute permission="playbook.read">
                  <Playbooks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/playbook-library"
              element={
                <ProtectedRoute roles={["admin", "cro"]}>
                  <PlaybookLibrary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/remediation"
              element={
                <ProtectedRoute anyPermission={["risk.read", "remediation.read"]}>
                  <Remediation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute permission="supplier.read" domain="t_thirdparty">
                  <Suppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signals"
              element={
                <ProtectedRoute permission="signal.read">
                  <Signals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute superuser>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute superuser>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="/governance"
              element={
                <ProtectedRoute permission="attestation.read">
                  <Governance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-log"
              element={
                <ProtectedRoute permission="audit.read">
                  <AuditLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/briefing-ledger"
              element={
                <ProtectedRoute permission="dashboard.read">
                  <BriefingLedger />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute superuser>
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants"
              element={
                <ProtectedRoute superuser>
                  <Tenants />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sales-docs"
              element={
                <ProtectedRoute superuser>
                  <SalesDocs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/automation"
              element={
                <ProtectedRoute superuser>
                  <Automation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reporting"
              element={
                <ProtectedRoute superuser>
                  <OwnerReporting />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance-ops"
              element={
                <ProtectedRoute superuser>
                  <ComplianceOps />
                </ProtectedRoute>
              }
            />
            <Route
              path="/strategic-ops"
              element={
                <ProtectedRoute superuser>
                  <StrategicOps />
                </ProtectedRoute>
              }
            />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/nps" element={<NpsLanding />} />
            <Route path="/risk-decisions" element={<RiskDecisions />} />
            <Route
              path="/owner/drift-watches"
              element={
                <ProtectedRoute superuser>
                  <DriftWatchOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path="/connectors"
              element={
                <ProtectedRoute>
                  <Connectors />
                </ProtectedRoute>
              }
            />
            <Route
              path="/security"
              element={
                <ProtectedRoute>
                  <Security />
                </ProtectedRoute>
              }
            />
            <Route
              path="/compliance-readiness"
              element={
                <ProtectedRoute superuser>
                  <ComplianceReadiness />
                </ProtectedRoute>
              }
            />
            <Route
              path="/provisioning"
              element={
                <ProtectedRoute superuser>
                  <Provisioning />
                </ProtectedRoute>
              }
            />
            <Route
              path="/access-policy"
              element={
                <ProtectedRoute roles={["admin", "org_admin"]}>
                  <AccessPolicy />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </HashSessionGate>
          <CookieConsent />
          </TourProvider>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </TranslationProvider>
        </AuthProvider>
        </ErioAuth0Provider>
      </HelmetProvider>
    </div>
  );
}

export default App;
